import {
  createBaseThemeOptions,
  createUnifiedTheme,
  palettes,
} from '@backstage/theme';
import {
  commonTypography,
  commonComponents,
  commonSidebarItemRootStyles,
  commonSidebarSubmenuItemStyles,
  commonSidebarLabelStyles,
  commonSidebarLinkResetStyles,
  createTableHeaderOverrides,
} from './common';

export interface DtThemeConfig {
  variant: 'light' | 'dark';
  background: { default: string; paper: string };
  text: { primary: string; secondary: string };
  primary: { main: string; light: string; dark: string; contrast: string };
  secondary?: { main: string; light: string; dark: string; contrast: string };
  border: string;
  divider?: string;
  accent: string;
  action: {
    active: string;
    hover: string;
    selected: string;
    disabled: string;
    disabledBackground: string;
  };
  nav: {
    background: string;
    text: string;
    selectedText?: string;
    itemHover: string;
    submenuBg: string;
  };
  header: {
    background: string;
    gradient?: string;
    shadow: string;
    borderBottom?: string;
    /** Defaults to '#ffffff'. Override for themes with coloured header text. */
    textColor?: string;
    linkHoverColor: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraComponents?: Record<string, any>;
}

export function createDtTheme(config: DtThemeConfig) {
  const { variant, background, text, primary, border, accent, action, nav, header } = config;
  const basePalette = variant === 'dark' ? palettes.dark : palettes.light;
  const divider = config.divider ?? border;
  const navSelectedText = nav.selectedText ?? accent;
  const headerTextColor = header.textColor ?? '#ffffff';
  const sec = config.secondary ?? {
    main: primary.light,
    light: primary.light,
    dark: primary.main,
    contrast: text.primary,
  };

  // Sidebar selected-item styles differ between light (accent text) and dark (accent border).
  const sidebarItemSelectedStyle =
    variant === 'light'
      ? {
          backgroundColor: nav.submenuBg,
          color: navSelectedText,
          fontWeight: 600,
          '&:hover': { backgroundColor: nav.itemHover },
          '& .MuiListItemIcon-root': { color: navSelectedText },
        }
      : {
          backgroundColor: background.paper,
          color: nav.text,
          fontWeight: 600,
          fontStyle: 'normal' as const,
          borderLeft: `3px solid ${accent}`,
          '&:hover': { backgroundColor: nav.itemHover },
          '& .MuiListItemIcon-root': { color: nav.text },
        };

  const submenuSelectedStyle =
    variant === 'light'
      ? {
          backgroundColor: nav.submenuBg,
          color: navSelectedText,
          '&:hover': { backgroundColor: nav.itemHover },
        }
      : {
          backgroundColor: background.paper,
          color: nav.text,
          fontWeight: 600,
          fontStyle: 'normal' as const,
          '&:hover': { backgroundColor: nav.itemHover },
        };

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...basePalette,
        border,
        background,
        text: {
          primary: text.primary,
          secondary: text.secondary,
        },
        action,
        // Backstage paints `textContrast` as text *on* the page background
        // (highlighted table headers, gauge values, graph labels), so it must
        // be the most readable foreground, not the background.
        textContrast: text.primary,
        textSubtle: text.secondary,
        primary: {
          main: primary.main,
          light: primary.light,
          dark: primary.dark,
          contrastText: primary.contrast,
        },
        secondary: {
          main: sec.main,
          light: sec.light,
          dark: sec.dark,
          contrastText: sec.contrast,
        },
        gold: accent,
        navigation: {
          background: nav.background,
          indicator: accent,
          color: nav.text,
          selectedColor: navSelectedText,
          navItem: { hoverBackground: nav.itemHover },
          submenu: { background: nav.submenuBg },
        },
        divider,
      },
    }),
    typography: commonTypography,
    components: {
      ...commonComponents,
      ...createTableHeaderOverrides({ idle: text.secondary, active: text.primary }),
      BackstageHeader: {
        styleOverrides: {
          header: () => ({
            backgroundColor: header.background,
            backgroundImage: header.gradient ?? 'none',
            minHeight: 112,
            boxShadow: header.shadow,
            ...(header.borderBottom ? { borderBottom: header.borderBottom } : {}),
            '& a': {
              color: `${headerTextColor} !important`,
              '&:hover': { color: `${header.linkHoverColor} !important` },
            },
            '& .MuiLink-root': {
              color: `${headerTextColor} !important`,
              '&:hover': { color: `${header.linkHoverColor} !important` },
            },
            '& .MuiTypography-colorInherit': {
              color: `${headerTextColor} !important`,
            },
          }),
          title: {
            color: headerTextColor,
            fontWeight: 700,
          },
        },
      },
      BackstageSidebarSubmenuItem: {
        styleOverrides: {
          item: {
            ...commonSidebarSubmenuItemStyles,
            color: nav.text,
            '&:hover': { backgroundColor: nav.itemHover },
          },
          selected: submenuSelectedStyle,
        },
      },
      BackstageSidebarItem: {
        styleOverrides: {
          root: {
            ...commonSidebarItemRootStyles,
            color: nav.text,
            '&.Mui-selected': sidebarItemSelectedStyle,
            '&:hover': {
              backgroundColor: nav.itemHover,
              transform: 'translateX(4px)',
            },
            '& .MuiListItemIcon-root': {
              ...commonSidebarItemRootStyles['& .MuiListItemIcon-root'],
              color: nav.text,
            },
          },
          label: { ...commonSidebarLabelStyles },
        },
      },
      MuiLink: {
        defaultProps: { underline: 'hover' as const },
        styleOverrides: {
          root: {
            textDecoration: 'none',
            fontWeight: 500,
            transition: 'all 0.2s ease-in-out',
            color: primary.main,
            '&:hover': { color: primary.dark, textDecoration: 'underline' },
            '&:active': { color: primary.dark },
            ...commonSidebarLinkResetStyles,
            '.BackstageHeader-header &': {
              color: `${headerTextColor} !important`,
              '&:hover': { color: `${header.linkHoverColor} !important` },
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: 'all 0.3s ease-in-out',
            ...(variant === 'dark'
              ? {
                  backgroundColor: background.paper,
                  boxShadow: `0 0 0 1px ${border}`,
                  border: 'none',
                  '&:hover': {
                    boxShadow: `0 0 0 1px ${primary.light}, 0 4px 20px ${primary.main}33`,
                  },
                }
              : {
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${border}`,
                  '&:hover': { boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' },
                }),
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            ...commonComponents.MuiChip.styleOverrides.root,
            color: primary.dark,
            backgroundColor: `${primary.light}33`,
            border: `1px solid ${primary.light}`,
            '&:hover': {
              backgroundColor: `${primary.light}66`,
              color: primary.dark,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
            textTransform: 'none' as const,
            boxShadow: 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)' },
          },
          contained: {
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              transform: 'translateY(-1px)',
            },
          },
          ...(variant === 'dark'
            ? {
                containedPrimary: {
                  backgroundColor: background.paper,
                  color: text.primary,
                  border: `1px solid ${border}`,
                  '&:hover': { backgroundColor: nav.itemHover, borderColor: primary.light },
                  '&.Mui-disabled': { backgroundColor: background.paper, color: text.secondary },
                },
                outlined: {
                  borderColor: border,
                  color: text.primary,
                  '&:hover': { borderColor: primary.light, backgroundColor: action.hover },
                },
              }
            : {}),
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            ...(variant === 'dark' ? { backgroundColor: background.paper } : {}),
            '&.MuiPaper-elevation1': {
              '& .MuiCardHeader-root': {
                backgroundColor: header.background,
                backgroundImage: header.gradient ?? 'none',
                color: headerTextColor,
                '& .MuiCardHeader-title, & .MuiTypography-h5': { color: headerTextColor },
                '& .MuiCardHeader-subheader, & .MuiTypography-body2': {
                  color: text.secondary,
                },
              },
            },
          },
          elevation1: {
            boxShadow:
              variant === 'dark' ? `0 0 0 1px ${border}` : '0 2px 8px rgba(0, 0, 0, 0.08)',
          },
          elevation2: {
            boxShadow:
              variant === 'dark'
                ? `0 0 0 1px ${border}, 0 4px 16px rgba(0, 0, 0, 0.24)`
                : '0 4px 16px rgba(0, 0, 0, 0.12)',
          },
        },
      },
      ...(variant === 'dark'
        ? {
            MuiTextField: {
              styleOverrides: {
                root: {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: border },
                    '&:hover fieldset': { borderColor: primary.light },
                    '&.Mui-focused fieldset': { borderColor: primary.main },
                  },
                  '& .MuiInputLabel-root': { color: text.secondary },
                  '& .MuiOutlinedInput-input': { color: text.primary },
                },
              },
            },
          }
        : {}),
      MuiCssBaseline: {
        styleOverrides: {
          '[class*="BackstageItemCardHeader-root"]': {
            backgroundColor: `${header.background} !important`,
            backgroundImage: `${header.gradient ?? 'none'} !important`,
            color: `${headerTextColor} !important`,
            '& .MuiTypography-root, & h3, & h4': { color: `${headerTextColor} !important` },
          },
          '[class*="makeStyles-header"]': {
            backgroundColor: `${header.background} !important`,
            backgroundImage: `${header.gradient ?? 'none'} !important`,
            color: `${headerTextColor} !important`,
            '& .MuiTypography-root, & h3, & h4': { color: `${headerTextColor} !important` },
          },
          '[class*="BackstageHeaderLabel-value"] .MuiLink-root': {
            color: `${headerTextColor} !important`,
            textDecoration: 'none !important',
          },
          '[class*="BackstageHeaderLabel-value"] .MuiLink-root:hover': {
            color: `${header.linkHoverColor} !important`,
            textDecoration: 'underline !important',
          },
          '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover': {
            color: `${headerTextColor} !important`,
            textDecoration: 'none !important',
          },
          '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover:hover': {
            color: `${header.linkHoverColor} !important`,
            textDecoration: 'underline !important',
          },
          '[class*="BackstageHeader-header"] .MuiLink-root, [class*="BackstageHeader-header"] .MuiLink-underlineHover':
            {
              color: `${headerTextColor} !important`,
            },
          '[class*="BackstageHeader-header"] .MuiLink-root:hover, [class*="BackstageHeader-header"] .MuiLink-underlineHover:hover':
            {
              color: `${header.linkHoverColor} !important`,
            },
          '[class*="BackstageHeader-header"] .MuiTypography-colorInherit': {
            color: `${headerTextColor} !important`,
          },
          'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a':
            {
              color: `${headerTextColor} !important`,
            },
          'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a:hover':
            {
              color: `${header.linkHoverColor} !important`,
            },
          '.MuiTypography-colorInherit.MuiLink-underlineHover': {
            color: `${headerTextColor} !important`,
          },
          body: {
            '& header[class*="BackstageHeader"] a': {
              color: `${headerTextColor} !important`,
            },
            '& header[class*="BackstageHeader"] a:hover': {
              color: `${header.linkHoverColor} !important`,
            },
          },
        },
      },
      CatalogReactUserListPicker: {
        styleOverrides: {
          root: {
            backgroundColor: background.paper,
            boxShadow:
              variant === 'dark' ? `0 0 0 1px ${border}` : '0 2px 8px rgba(0, 0, 0, 0.08)',
            ...(variant === 'light' ? { border: `1px solid ${border}` } : {}),
            borderRadius: 8,
          },
          title: { color: text.secondary },
          listIcon: { color: primary.main },
          menuItem: {
            color: text.primary,
            '&:nth-of-type(odd)': {
              backgroundColor: variant === 'dark' ? background.default : background.paper,
            },
            '&:nth-of-type(even)': { backgroundColor: background.default },
            '&.Mui-selected': {
              backgroundColor: `${primary.main}33`,
              color: variant === 'dark' ? primary.light : primary.dark,
              '&:hover': { backgroundColor: `${primary.main}4d` },
            },
            '&:hover': { backgroundColor: `${primary.main}1a` },
          },
          groupWrapper: {
            backgroundColor: background.default,
            boxShadow: variant === 'dark' ? `0 0 0 1px ${border}` : 'none',
            ...(variant === 'light' ? { border: `1px solid ${border}` } : {}),
            borderRadius: 6,
          },
        },
      },
      ...(config.extraComponents ?? {}),
    },
  });
}
